import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Video } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const LOGO_URL = "/logo-white.png";

const Avatar = ({ initials, color }) => (
  <span
    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-ink ${color}`}
  >
    {initials}
  </span>
);

const ScreenConnexion = () => (
  <div className="flex h-full flex-col items-center justify-center gap-3 bg-[linear-gradient(160deg,#2BC97E,#00C4CC)] px-5">
    <img src={LOGO_URL} alt="Syndicappli" className="h-9 w-auto" />
    <div className="mt-2 w-full rounded-2xl bg-white p-4 shadow-lg">
      <p className="text-center text-base font-semibold text-ink">Connexion</p>
      <div className="mt-3 space-y-2.5">
        <div>
          <p className="text-[10px] font-medium text-ink/70">Email</p>
          <div className="mt-1 h-8 rounded-lg border border-[#E2E6EA]" />
        </div>
        <div>
          <p className="text-[10px] font-medium text-ink/70">Mot de passe</p>
          <div className="mt-1 h-8 rounded-lg border border-[#E2E6EA]" />
        </div>
        <div className="flex h-9 items-center justify-center rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] text-xs font-semibold text-white">
          Se connecter
        </div>
      </div>
      <p className="mt-2.5 text-center text-[9px] font-semibold text-electric underline">
        J'ai oublié mon mot de passe
      </p>
    </div>
  </div>
);

const ScreenAccueil = () => (
  <div className="flex h-full flex-col bg-mist">
    <div className="border-b border-black/5 bg-white px-4 pb-2.5 pt-9">
      <p className="text-sm font-semibold text-ink">Bonjour John</p>
      <p className="truncate text-[9px] text-ink/40">
        Immeuble — 36 rue Hector Berlioz, Agen
      </p>
    </div>
    <div className="flex-1 space-y-2.5 p-3">
      <div className="rounded-2xl bg-white p-3 shadow-sm">
        <p className="text-[10px] font-semibold text-ink">Avril 2020</p>
        <div className="mt-2 grid grid-cols-7 gap-1 text-center">
          {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
            <span key={i} className="text-[7px] font-semibold text-ink/35">{d}</span>
          ))}
          {[5, 6, 7, 8, 9, 10, 11].map((n) => (
            <span
              key={n}
              className={`mx-auto flex h-5 w-5 items-center justify-center rounded-full text-[8px] font-semibold ${
                n === 8
                  ? "bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] text-white"
                  : "text-ink/60"
              }`}
            >
              {n}
            </span>
          ))}
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-[9px] font-semibold text-ink/60">
          <span className="h-1.5 w-1.5 rounded-full bg-gold" />
          Assemblée Générale — jeudi 8
        </p>
      </div>
      <div className="rounded-2xl bg-white p-3 shadow-sm">
        <p className="text-[8px] font-bold uppercase tracking-wider text-gold">
          Dernier incident
        </p>
        <p className="mt-0.5 text-[11px] font-semibold text-ink">Coupure de courant</p>
        <p className="text-[8px] text-ink/40">12 Février 2020 · En cours</p>
      </div>
      <div className="rounded-2xl bg-white p-3 shadow-sm">
        <p className="text-[8px] font-bold uppercase tracking-wider text-electric">
          Actualité
        </p>
        <p className="mt-0.5 text-[10px] font-semibold text-ink">
          Travaux de ravalement votés
        </p>
        <div className="mt-1.5 h-1.5 w-3/4 rounded-full bg-mist" />
        <div className="mt-1 h-1.5 w-1/2 rounded-full bg-mist" />
      </div>
    </div>
  </div>
);

const ScreenMessagerie = () => (
  <div className="flex h-full flex-col bg-white">
    <div className="border-b border-black/5 px-4 pb-2.5 pt-9">
      <p className="text-sm font-semibold text-ink">Messagerie</p>
      <p className="truncate text-[9px] text-ink/40">
        Immeuble — 36 rue Hector Berlioz, Agen
      </p>
    </div>
    <div className="flex-1 space-y-2.5 bg-mist p-3">
      <div className="flex items-start gap-2">
        <Avatar initials="MC" color="bg-[#F5E960]" />
        <p className="max-w-[165px] rounded-2xl rounded-tl-md bg-[#F1F2F4] px-3 py-2 text-[11px] leading-snug text-ink">
          Bonjour, l'ascenseur est en panne, pouvez-vous envoyer quelqu'un ?
        </p>
      </div>
      <div className="flex items-start justify-end gap-2">
        <p className="max-w-[165px] rounded-2xl rounded-tr-md bg-bubble px-3 py-2 text-[11px] leading-snug text-ink">
          Bien sûr Marc, un technicien passe demain à 9h.
        </p>
        <Avatar initials="JD" color="bg-[#BFE3F7]" />
      </div>
      <div className="flex items-center gap-2">
        <Avatar initials="MC" color="bg-[#F5E960]" />
        <span className="flex items-center gap-1 rounded-2xl rounded-tl-md bg-[#F1F2F4] px-3 py-2.5">
          <span className="h-1 w-1 animate-pulse rounded-full bg-ink/40" />
          <span className="h-1 w-1 animate-pulse rounded-full bg-ink/40 [animation-delay:150ms]" />
          <span className="h-1 w-1 animate-pulse rounded-full bg-ink/40 [animation-delay:300ms]" />
        </span>
      </div>
    </div>
    <div className="flex items-center gap-2 px-3 py-2.5">
      <div className="h-8 flex-1 rounded-full border border-black/10 px-3 text-[10px] leading-8 text-ink/35">
        Répondre...
      </div>
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)]">
        <Send size={12} className="text-white" />
      </div>
    </div>
  </div>
);

const ScreenComptabilite = () => (
  <div className="flex h-full flex-col bg-white">
    <div className="border-b border-black/5 px-4 pb-2.5 pt-9">
      <p className="text-sm font-semibold text-ink">Comptabilité</p>
      <p className="text-[9px] text-ink/40">Approbation des dépenses</p>
    </div>
    <div className="flex-1 space-y-2.5 bg-mist p-3">
      <div className="rounded-2xl bg-[linear-gradient(135deg,#2BC97E,#00C4CC)] p-3.5 text-white">
        <p className="text-[9px] uppercase tracking-wider opacity-80">
          Solde de la copropriété
        </p>
        <p className="text-xl font-bold">12 450 €</p>
        <div className="mt-2 flex h-10 items-end gap-1.5">
          {[40, 65, 50, 80, 60, 95, 75].map((hgt, i) => (
            <div
              key={i}
              style={{ height: `${hgt}%` }}
              className={`w-full rounded-sm ${i === 5 ? "bg-white" : "bg-white/40"}`}
            />
          ))}
        </div>
      </div>
      <div className="space-y-2 rounded-2xl bg-white p-3 shadow-sm">
        {[
          { dot: "bg-[#2FCB74]", label: "Électricité — EDF", status: "Payé", sc: "text-electric" },
          { dot: "bg-gold", label: "Travaux ascenseur", status: "En cours", sc: "text-gold" },
          { dot: "bg-[#2FCB74]", label: "Assurance immeuble", status: "Payé", sc: "text-electric" },
        ].map((r) => (
          <div key={r.label} className="flex items-center gap-2">
            <span className={`h-1.5 w-1.5 rounded-full ${r.dot}`} />
            <span className="flex-1 truncate text-[10px] font-medium text-ink/70">{r.label}</span>
            <span className={`text-[9px] font-bold ${r.sc}`}>{r.status}</span>
          </div>
        ))}
      </div>
      <div className="flex h-9 items-center justify-center gap-2 rounded-full border border-electric/30 text-[10px] font-semibold text-electric">
        <Video size={12} />
        Export comptable en un clic
      </div>
    </div>
  </div>
);

const SCREENS = [
  { id: "connexion", label: "Connexion", C: ScreenConnexion },
  { id: "accueil", label: "Accueil", C: ScreenAccueil },
  { id: "messagerie", label: "Messagerie", C: ScreenMessagerie },
  { id: "comptabilite", label: "Comptabilité", C: ScreenComptabilite },
];

export const PHONE_SCREENS = SCREENS;

export const PhoneMockup = ({ index = null }) => {
  const [autoIdx, setAutoIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const controlled = index !== null;

  useEffect(() => {
    if (controlled || paused) return undefined;
    const t = setInterval(() => setAutoIdx((i) => (i + 1) % SCREENS.length), 3600);
    return () => clearInterval(t);
  }, [controlled, paused]);

  const idx = controlled ? index % SCREENS.length : autoIdx;
  const Active = SCREENS[idx].C;

  return (
    <div
      className="relative mx-auto w-[260px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="phone-mockup"
    >
      <div className="rounded-[2.9rem] border-[9px] border-[#2D3436] bg-[#2D3436] shadow-[0_50px_100px_-30px_rgba(0,55,70,0.6)]">
        <div className="relative h-[540px] overflow-hidden rounded-[2.2rem] bg-white">
          <div className="absolute left-1/2 top-2 z-20 h-[18px] w-16 -translate-x-1/2 rounded-full bg-[#2D3436]" />
          <AnimatePresence initial={false}>
            <motion.div
              key={SCREENS[idx].id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.55, ease: EASE }}
              className="absolute inset-0 h-full bg-white"
              data-testid={`phone-screen-${SCREENS[idx].id}`}
            >
              <Active />
            </motion.div>
          </AnimatePresence>
          <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
            {SCREENS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setAutoIdx(i)}
                aria-label={`Écran ${s.label}`}
                data-testid={`phone-dot-${s.id}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === idx
                    ? "w-5 bg-[linear-gradient(100deg,#00C6CC,#2FCB74)]"
                    : "w-1.5 bg-ink/25"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-white">
        {SCREENS[idx].label}
      </p>
    </div>
  );
};
